import { useEffect } from 'react';
import { MouseEventHandler, MouseEvent, useState, Dispatch, SetStateAction } from 'react';
import { GameState, GridState, GamePlayer, PlayerX, GameSetup } from '../../utils';
import Button from '../Button';
import Confirmation from '../Confirmation';
import Grid from '../Grid';
import Logo from '../Logo';
import Modal from '../Modal';
import Result from '../Result';
import Score from '../Score';
import Turn from '../Turn';

import styles from './styles.module.css';

interface GameProps {
  state: GameState;
  setup: GameSetup;
  setGameSetup: Dispatch<SetStateAction<GameSetup>>;
  setGameState: Dispatch<SetStateAction<GameState>>;
}



const Game = (props: GameProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showResult, setShowResult] = useState(false);

  const [gridState, setGridState] = useState<GridState>([
    [undefined, undefined, undefined],
    [undefined, undefined, undefined],
    [undefined, undefined, undefined]]
  );

  const [currTurn, setCurrTurn] = useState(PlayerX);

  const setGridMark = (row: number, col: number, mark: GamePlayer) => {
    const newState = gridState;
    newState[row][col] = mark;
    updateGridState(newState);
  };

  const updateGridState = (newState: GridState) => {
    setGridState([
      [newState[0][0], newState[0][1], newState[0][2]],
      [newState[1][0], newState[1][1], newState[1][2]],
      [newState[2][0], newState[2][1], newState[2][2]]]);
  };

  const checkWinningConditions = () => {
    const winningStates = [
      [gridState[0][0], gridState[0][1], gridState[0][2]],
      [gridState[1][0], gridState[1][1], gridState[1][2]],
      [gridState[2][0], gridState[2][1], gridState[2][2]],
      [gridState[0][0], gridState[1][0], gridState[2][0]],
      [gridState[0][1], gridState[1][1], gridState[2][1]],
      [gridState[0][2], gridState[1][2], gridState[2][2]],
      [gridState[0][0], gridState[1][1], gridState[2][2]],
      [gridState[0][2], gridState[1][1], gridState[2][0]],
    ];
    const wins = (player: GamePlayer) => winningStates.some((state) =>
      state.every(mark => mark === player));
    if (wins(GamePlayer.X)) {
      props.setGameState(state => ({ status: 'finished', winner: GamePlayer.X }));
      props.setGameSetup(setup => ({ ...setup, score: { ...setup.score, X: setup.score.X + 1} }));
      return;
    }
    if (wins(GamePlayer.O)) {
      props.setGameState(state => ({ status: 'finished', winner: GamePlayer.O }));
      props.setGameSetup(setup => ({ ...setup, score: { ...setup.score, O: setup.score.O + 1} }));

      return;
    }
    const finished = gridState.every(row => row.every(mark => mark !== undefined));
    props.setGameState(state => ({ status: finished ? 'finished' : 'playing', winner: undefined }));
    if(finished) {
      props.setGameSetup(setup => ({ ...setup, score: { ...setup.score, ties: setup.score.ties + 1} }));
    }
  };

  const restartGame = () => {
    props.setGameState(state => ({ status: 'playing', winner: undefined }));
    setShowConfirmation(false);
    setShowResult(false);
    setCurrTurn(GamePlayer.X);
    setGridState([
      [undefined, undefined, undefined],
      [undefined, undefined, undefined],
      [undefined, undefined, undefined]]);
  };

  const quitGame = () => {
    setShowConfirmation(false);
    setShowResult(false);
    restartGame();
    props.setGameSetup(setup => ({
      ...setup,
      score: {
        X: 0,
        O: 0,
        ties: 0,
      },
      started: false,
    }));
  };

  const nextTurn = () => {
    checkWinningConditions();
    if (props.state.status === 'playing') {
      setCurrTurn(currTurn === GamePlayer.X ? GamePlayer.O : GamePlayer.X);
    } else {
      showModal('result');
    }
  };

  useEffect(() => {
    if (props.state.status === 'finished') {
      showModal('result');
    }
  });

  const OnGridCellClick: MouseEventHandler<HTMLDivElement> = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const [row, col, mark] = [
      event.currentTarget.getAttribute('data-row'),
      event.currentTarget.getAttribute('data-col'),
      event.currentTarget.getAttribute('data-mark')
    ];
    if ((props.state.status === 'playing') && row && col && !mark) {
      setGridMark(+row, +col, currTurn);
    }
    nextTurn();
  };

  const OnRestartClick: MouseEventHandler<HTMLDivElement> = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    showModal('confirmation');
  };

  const OnModalClick = (handler?: any): MouseEventHandler => (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    closeModal();
    if (handler) handler();
  };

  const showModal = (modal: 'confirmation' | 'result') => {
    setModalOpen(true);
    setShowConfirmation(modal === 'confirmation');
    setShowResult(modal === 'result');
  };

  const closeModal = () => {
    setModalOpen(false);
    setShowConfirmation(false);
    setShowResult(false);
  };

  return (
    <>
      <Modal isOpen={modalOpen}>
        {showConfirmation &&
          <Confirmation
            cancelHandler={OnModalClick()}
            confirmHandler={OnModalClick(restartGame)}>
            Restart game?
          </Confirmation>}
        {showResult &&
          <Result
            winner={props.state.winner}
            mark={props.setup.mark}
            versus={props.setup.versus}
            cancelHandler={OnModalClick(quitGame)}
            confirmHandler={OnModalClick(restartGame)} />}
      </Modal>
      <div className={styles['game']}>
        <div className={styles['header']}>
          <Logo />
          <Turn turn={currTurn} />
          <Button color='silver' secondary icon onClick={OnRestartClick} />
        </div>
        <Grid state={gridState} onCellClick={OnGridCellClick} />
        <div className={styles['score']}>
          <Score label='X (P2)' color='blue' value={props.setup.score.X.toString()} />
          <Score label='TIES' color='silver' value={props.setup.score.ties.toString()} />
          <Score label='O (P1)' color='yellow' value={props.setup.score.O.toString()} />
        </div>
      </div>
    </>
  );
};

export default Game;